import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  styled,
  tableCellClasses,
  FormControl,
  Collapse,
  Autocomplete,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import { axiosRequest } from "../../../service/AxiosService";
import {
  GroupVersionModel,
  GroupVersions,
} from "../../../type/response/GroupManagementInitResponse";
import { ExpandMoreButton } from "../GroupManagement";
import { Delete } from "@mui/icons-material";

/**
 * テーブルのスタイル(セル)を設定
 */
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: "nowrap", // 改行しない
  },
}));

/**
 * テーブルのスタイル(行)を設定
 */
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

/**
 * フォーム初期値
 */
const formDefaultValues: GroupVersionModel = {
  groupVersion: {
    id: null,
    version: "",
    date: null,
    isEnable: false,
    copyFromVersionId: null,
  },
  oldGroupVersion: null,
};

/**
 * グループバージョン管理ダイアログコンポーネント
 * - グループバージョンの新規追加・既存バージョンの更新（名称・開始日・有効化）を行う
 * - 有効化チェックは必ず1件のみ設定できる
 * - props: open (ダイアログ表示状態), onClose (閉じるコールバック),
 *          groupVersionList (既存バージョン一覧)
 */
export default function EditGroupVersionDialog({
  open,
  onClose,
  groupVersionList,
}: {
  open: boolean;
  onClose: (action: boolean) => void;
  groupVersionList: GroupVersions[];
}) {
  const form = useForm<GroupVersionModel>({ defaultValues: formDefaultValues });
  const {
    control,
    formState: { errors },
    getValues,
    reset,
    handleSubmit,
  } = form;
  const [expanded, setExpanded] = useState(true);
  const addCount = useRef(0);
  const isSubmitting = useRef<boolean>(false);

  /** 冪等性キー：ダイアログを開くたびに新しいUUIDを生成し、閉じると破棄する */
  const idempotencyKeyRef = useRef<string>("");
  useEffect(() => {
    if (open) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
  }, [open]);
  const [rows, setRows] = useState<GroupVersionModel[]>([]);
  const [createRows, setCreateRows] = useState<string[]>([]);
  const [isNotEnabledFlag, setIsNotEnabledFlag] = useState<boolean>(false);

  /**
   * 初期処理
   */
  useEffect(() => {
    setRows(
      groupVersionList.map(
        (v) => ({
          groupVersion: v,
          oldGroupVersion: v.copyFromVersionId
            ? (groupVersionList.find((gv) => gv.id === v.copyFromVersionId) ?? null)
            : null,
        }) as GroupVersionModel
      )
    );
    reset(formDefaultValues);
  }, [groupVersionList, reset]);

  /**
   * 画面を閉じたらフォームをリセットする
   */
  const handleClose = (action: boolean) => {
    setRows(
      groupVersionList.map(
        (v) => ({
          groupVersion: v,
          oldGroupVersion: v.copyFromVersionId
            ? (groupVersionList.find((gv) => gv.id === v.copyFromVersionId) ?? null)
            : null,
        }) as GroupVersionModel
      )
    );
    reset(formDefaultValues);
    setIsNotEnabledFlag(false);
    onClose(action);
  };

  /**
   * 登録・更新された行をメモ化
   */
  const createOrUpdateList: GroupVersionModel[] = useMemo(() => {
    return rows.reduce<GroupVersionModel[]>((acc, row) => {
      const initRow = groupVersionList.find((g) => g.id === row.groupVersion.id) ?? null;
      if (!initRow) {
        // 新規追加されたデータの場合、コピーを作成してidをnullにして返す（stateを直接変更しない）
        acc.push({ ...row, groupVersion: { ...row.groupVersion, id: null } });
      } else if (
        initRow.version !== row.groupVersion.version ||
        (!initRow.date && row.groupVersion.date) ||
        (initRow.date && !row.groupVersion.date) ||
        (initRow.date &&
          row.groupVersion.date &&
          !dayjs(initRow.date).isSame(row.groupVersion.date, "day")) ||
        initRow.isEnable !== row.groupVersion.isEnable
      ) {
        acc.push(row);
      }
      return acc;
    }, []);
  }, [groupVersionList, rows]);

  /**
   * グループバージョン登録・更新
   */
  const createOrUpdateGroupVersion = async () => {
    if (isSubmitting.current) return;

    // 有効化チェック有無を確認するため、更新後のgroupVersionの値を保持する
    const updatedGroupVersionList = [
      // 既存バージョン（変更があれば更新後の値に置き換える）
      ...groupVersionList.map(group => {
        const match = createOrUpdateList.find(
          item => item.groupVersion.id === group.id
        );
        return match ? match.groupVersion : group;
      }),
      // 新規追加バージョン（idがnullのもの）
      ...createOrUpdateList
        .filter(item => item.groupVersion.id === null)
        .map(item => item.groupVersion),
    ];

    // 有効化チェックの数を取得する
    const hasEnabledLength = updatedGroupVersionList.filter(item => item.isEnable).length;

    // 有効化チェックが1つ以外の場合は、エラーメッセージを出力
    if (!(hasEnabledLength === 1)) {
      setIsNotEnabledFlag(true);
      return;
    }

    isSubmitting.current = true;
    try {
      const response = await axiosRequest<string>(
        "/groups/createOrUpdateGroupVersion",
        "POST",
        { data: { groupVersionList: createOrUpdateList,
                  hasEnabledLength: hasEnabledLength },
          params: { "Idempotency-Key": idempotencyKeyRef.current } }
      );
      if (response) handleClose(true);
    } catch (e) {
      console.error(e);
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>グループバージョン管理</DialogTitle>
      {/* isNotEnabledFlag が true のときだけ赤文字Typographyを表示 */}
      {isNotEnabledFlag && (
        <Typography
        variant="body1"
        sx={{ 
          mt: 2,
          ml: 3,
          color: "red",
          fontWeight: "bold",
        }}
      >
        グループバージョンの有効/無効化の設定が不適切です。<br></br>
        有効化するグループバージョンを一つ選択してください。
      </Typography>
        )
      }
      <DialogContent>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Grid container sx={{ gap: 2, alignItems: "center", my: 1 }}>
            <Grid>
              <Controller
                control={control}
                name="groupVersion.version"
                rules={{
                  required: "この項目は入力必須です。",
                  validate: (data) => {
                    if (rows.some((v) => v.groupVersion.version === data)) {
                      return "このバージョン名は登録されています。";
                    }
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    error={!!errors.groupVersion?.version}
                    helperText={errors.groupVersion?.version?.message}
                    label="バージョン名"
                    sx={{ width: 200 }}
                  />
                )}
              />
            </Grid>
            <Grid>
              <Controller
                control={control}
                name="groupVersion.date"
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="YYYY/MM/DD"
                      label="開始日"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value) =>
                        field.onChange(value ? value.toDate() : null)
                      }
                      slotProps={{ calendarHeader: { format: "YYYY年M月" } }}
                      sx={{ width: 200 }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            <Grid>
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name="groupVersion.isEnable"
                    render={({ field }) => (
                      <Checkbox
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                }
                label="有効"
              />
            </Grid>
            <Grid>
              <Controller
                control={control}
                name="oldGroupVersion"
                render={({ field }) => (
                  <Autocomplete
                    options={groupVersionList}
                    getOptionLabel={(option) => option.version}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="旧バージョンをコピー"
                        sx={{ width: 240 }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid
              size={12}
              sx={{ display: "flex", justifyContent: "flex-end", mx: 1 }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit(() => {
                  addCount.current += 1;
                  const newRow: GroupVersionModel = {
                    ...getValues(),
                    groupVersion: {
                      ...getValues("groupVersion"),
                      id: `${addCount.current}`,
                    },
                  };
                  setRows((rows) => [...rows, newRow]);
                  setCreateRows((prev) => [
                    ...prev.filter((v) => v !== (newRow.groupVersion.id ?? "")),
                    newRow.groupVersion.id ?? "",
                  ]);
                })}
              >
                追加
              </Button>
            </Grid>
          </Grid>
        </Collapse>
        <Divider>
          <ExpandMoreButton
            expanded={expanded}
            withStyled={false}
            onClick={() => setExpanded((prev) => !prev)}
          />
        </Divider>
        <TableContainer component={Paper} sx={{ mt: 1 }}>
          <Table>
            <TableHead>
              <StyledTableRow>
                <StyledTableCell />
                <StyledTableCell>バージョン名</StyledTableCell>
                <StyledTableCell>開始日</StyledTableCell>
                <StyledTableCell>有効/無効</StyledTableCell>
                <StyledTableCell>コピーバージョン</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <StyledTableRow key={row.groupVersion.id}>
                  <StyledTableCell>
                    {createRows.includes(row.groupVersion.id ?? "") ? (
                      <IconButton
                        type="button"
                        onClick={() => {
                          setRows((prev) =>
                            prev.filter(
                              (v) => v.groupVersion.id !== row.groupVersion.id
                            )
                          );
                          setCreateRows((prev) =>
                            prev.filter((v) => v !== row.groupVersion.id)
                          );
                        }}
                      >
                        <Delete />
                      </IconButton>
                    ) : (
                      <Box sx={{ width: 36, height: 36 }} />
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <TextField
                      value={row.groupVersion.version}
                      onChange={(e) =>
                        setRows((prevRows) =>
                          prevRows.map((prevRow) =>
                            row.groupVersion.id === prevRow.groupVersion.id
                              ? ({
                                  ...row,
                                  groupVersion: {
                                    ...row.groupVersion,
                                    version: e.target.value,
                                  },
                                } as GroupVersionModel)
                              : prevRow
                          )
                        )
                      }
                      size="small"
                      sx={{ width: 200 }}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="YYYY/MM/DD"
                        label="開始日"
                        value={
                          row.groupVersion.date
                            ? dayjs(row.groupVersion.date)
                            : null
                        }
                        onChange={(value) =>
                          setRows((prevRows) =>
                            prevRows.map((prevRow) =>
                              row.groupVersion.id === prevRow.groupVersion.id
                                ? ({
                                    ...row,
                                    groupVersion: {
                                      ...row.groupVersion,
                                      date: value ? value.toDate() : null,
                                    },
                                  } as GroupVersionModel)
                                : prevRow
                            )
                          )
                        }
                        slotProps={{
                          calendarHeader: { format: "YYYY年M月" },
                          textField: { size: "small" },
                        }}
                        sx={{ width: 200 }}
                      />
                    </LocalizationProvider>
                  </StyledTableCell>
                  <StyledTableCell>
                    <FormControl>
                      <Checkbox
                        checked={row.groupVersion.isEnable}
                        onChange={(e) =>
                          setRows((prevRows) =>
                            prevRows.map((prevRow) =>
                              row.groupVersion.id === prevRow.groupVersion.id
                                ? ({
                                    ...row,
                                    groupVersion: {
                                      ...row.groupVersion,
                                      isEnable: e.target.checked,
                                    },
                                  } as GroupVersionModel)
                                : prevRow
                            )
                          )
                        }
                      />
                    </FormControl>
                  </StyledTableCell>
                  <StyledTableCell>
                    {createRows.includes(row.groupVersion.id ?? "") ? (
                      <Autocomplete
                        options={groupVersionList}
                        getOptionLabel={(option) => option.version}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={row.oldGroupVersion}
                        onChange={(_, value) => {
                          setRows((prevRows) =>
                            prevRows.map((prevRow) =>
                              row.groupVersion.id === prevRow.groupVersion.id
                                ? ({
                                    ...row,
                                    oldGroupVersion: value,
                                  } as GroupVersionModel)
                                : prevRow
                            )
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{ width: 240 }}
                            size="small"
                          />
                        )}
                      />
                    ) : (
                      <Box sx={{ width: 240, height: 36, display: "flex", alignItems: "center" }}>
                        <Typography variant="body2">
                          {row.oldGroupVersion?.version ?? ""}
                        </Typography>
                      </Box>
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button variant="outlined" onClick={() => handleClose(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={() => createOrUpdateGroupVersion()}
            disabled={createOrUpdateList.length <= 0}
          >
            登録
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
